import React, { useEffect } from "react";
import isNil from "lodash/isNil";
import qs from "qs";
import { useSelector } from "react-redux";

import { Action, Category, Label } from "constants/events";
import { CartDealGroup, CartItem, TaxType } from "custom-types/Cart";
import { useEventTracker } from "hooks/useEventTracker";
import useWindowSize from "hooks/useWindowSize";
import {
  getCart,
  getCartChanges,
  getCartIsInStore,
  getCartSessionId,
  getCartTotalByOrderTypeSelector,
  getCurrentStatusCanProceedToCheckout,
  getCurrentStatusCheckoutBlockedReason,
  getDeliveryMinimumCartInfo,
  getSelectedForDeliveryOption,
} from "redux/selectors/cart";
import {
  getCartDispensaryData,
  getCurrentStatusIsOpenForDelivery,
  getDeliveryServiceArea,
  getMenuLink,
  getShowDeliveryOption,
} from "redux/selectors/cartDispensary";
import { getGoogleMapsDirectionsUrl } from "redux/selectors/dispensary";
import { getCountryCode } from "redux/selectors/location";
import { getIsLoggedIn, getUserId } from "redux/selectors/user";
import currencyFormatter from "utils/currencyFormatter";
import logError from "utils/logError";
import { trackCheckoutEvent } from "utils/trackEvent";

import AlertBanner from "components/botanic/AlertBanner";
import Button from "components/botanic/Button";
import AlertIcon from "components/Icons/alert.svg";
import Prop65Message from "components/Prop65Message";
import ReorderCarousel from "components/ReorderCarousel";
import TrackImpression from "components/TrackImpression";

import BagMessage from "../BagMessage";
import CartTotals from "../CartTotals";
import CheckoutInstructions from "../CheckoutInstructions";
import DealGroupContainer from "../DealGroupContainer";
import DispensaryDirectionsButton from "../DispensaryDirectionsButton";
import FinePrint from "../FinePrint";
import FreeDeliveryStatus from "../FreeDeliveryStatus";
import FulfillmentCards from "../FulfillmentCards";
import InvalidItemsNotification from "../InvalidItemsNotification";
import ProceedToCheckoutButton from "../ProceedToCheckoutButton";
import PromoCodeCTA from "../PromoCodeCTA/PromoCodeCTA";
import PromoCodeInput from "../PromoCodeInput/PromoCodeInput";
import BagHeader from "./BagHeader";
import EmptyBagMessage from "./EmptyBagMessage";

type Props = {
  cartItems: CartItem[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
  cartItemErrors: any[];
  dealGroups: CartDealGroup[];
  isEmbedded?: boolean;
  referrer?: string;
  embeddedMenuUrl?: string;
};

const CART_ITEM_CHANGE_ERROR_PRIORITY = Object.freeze([
  "ItemUnavailableError",
  "NotReservableError",
]);

const InStoreOrderingMessage = () => {
  return (
    <CheckoutInstructions
      className="whitespace-pre-line"
      data-testid="in-store-ordering-message"
      title="How to complete your order:"
      subtitle="This dispensary doesn't offer online ordering, but don't sweat it!"
      message={`Simply show the budtender this cart and they'll complete your order in store. \n\nAnd if a product is no longer available, no worries! Our budtenders have your back with awesome alternatives to give you a great match.`}
    />
  );
};

const Bag = ({
  cartItems,
  cartItemErrors,
  dealGroups,
  isEmbedded,
  referrer,
  embeddedMenuUrl,
}: Props) => {
  const { publishEvent } = useEventTracker();
  const { isMobile } = useWindowSize();

  const cart = useSelector(getCart);
  const cartChanges = useSelector(getCartChanges);
  const canProceedToCheckout = useSelector(
    getCurrentStatusCanProceedToCheckout
  );
  const countryCode = useSelector(getCountryCode);
  const dispensary = useSelector(getCartDispensaryData);
  const dispensaryTaxType = dispensary?.taxType;
  const selectedForDeliveryOption = useSelector(getSelectedForDeliveryOption);
  const showDeliveryOption = useSelector(getShowDeliveryOption);
  const openForDelivery = useSelector(getCurrentStatusIsOpenForDelivery);
  const deliveryMinInfo = useSelector(getDeliveryMinimumCartInfo);
  const deliveryServiceArea = useSelector(getDeliveryServiceArea);
  const getCartTotalByOrderType = useSelector(getCartTotalByOrderTypeSelector);
  const orderIsDelivery = showDeliveryOption && selectedForDeliveryOption;
  const cartTotals = getCartTotalByOrderType(!!orderIsDelivery);
  const invalidCartItems = cartChanges.filter((change) =>
    CART_ITEM_CHANGE_ERROR_PRIORITY.includes(change.error)
  );
  const menuLink = useSelector(getMenuLink);
  const cartSessionId = useSelector(getCartSessionId);
  const cartIsInStore = useSelector(getCartIsInStore);
  const userId = useSelector(getUserId);
  const isLoggedIn = useSelector(getIsLoggedIn);
  const checkoutBlockedReason = useSelector(
    getCurrentStatusCheckoutBlockedReason
  );
  const googleMapsDirectionsUrl = useSelector(getGoogleMapsDirectionsUrl);

  const country = dispensary?.country || countryCode;
  const cartItemsExist = !!cart?.data?.cartItems.length;
  const hasNotMetMinimumForDelivery =
    cartItemsExist &&
    showDeliveryOption &&
    openForDelivery &&
    !deliveryMinInfo.hasMetMin;

  const postSavingsSubTotal =
    Number(cartTotals.subtotal) - Number(cartTotals.savings);

  const shouldRenderFreeDeliveryBanner = React.useMemo(() => {
    if (
      !orderIsDelivery ||
      postSavingsSubTotal <
        Number(deliveryServiceArea?.orderMinimum ?? Infinity) ||
      hasNotMetMinimumForDelivery
    ) {
      return false;
    }
    return (
      !isNil(cartTotals.subtotal) &&
      !isNil(deliveryServiceArea?.freeDeliveryMinimum)
    );
  }, [cartTotals, deliveryServiceArea]);

  let returnHref = menuLink;
  let embeddedQuery = "";
  if (isEmbedded && referrer) {
    let referrerUrl;
    try {
      referrerUrl = new URL(referrer);
      const baseUrl = referrerUrl.origin + referrerUrl.pathname;
      returnHref = `${baseUrl}?${embeddedQuery}`;
      const referrerQuery = qs.parse(referrerUrl.search);
      referrerQuery.cartId = cartSessionId;
      embeddedQuery = `${qs.stringify(referrerQuery)}`;
    } catch {
      logError("Error creating new referrerURL in Bag");
    }
  }

  useEffect(() => {
    if (dispensary?.scheduledDeliveryEnabled) {
      publishEvent({
        action: Action.impression,
        category: Category.checkoutPage,
        // @ts-ignore
        dispensaryId: dispensary?.id,
        label: Label.scheduleEnabled,
      });
    }

    if (!cartItemsExist) {
      fetch("/api/metrics/empty-bag-pageview", {
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        method: "POST",
      });
    }
  }, []);

  useEffect(() => {
    let label;
    switch (dispensaryTaxType) {
      case TaxType.CalculatedAtPayment:
        label = "tax not calculated";
        break;
      case TaxType.IncludedInMenu:
        label = "tax included";
        break;
      default:
        label = "tax estimated";
        break;
    }

    trackCheckoutEvent(
      "impression",
      label,
      {
        category: "tax",
        userId,
      },
      true
    );
  }, [userId, dispensaryTaxType]);

  useEffect(() => {
    const aCartItem = cartItems && cartItems[0] ? cartItems[0] : null;
    let reason;
    if (checkoutBlockedReason === "delivery_min_not_met") {
      reason = "checkout closed order min not met";
    } else if (checkoutBlockedReason === "dispensary_closed") {
      reason = "checkout closed outside ordering hours";
    } else if (checkoutBlockedReason === "convertible_cart") {
      reason = "checkout closed convertible cart";
    }
    if (reason) {
      trackCheckoutEvent("Impression", reason, {
        cartItem: aCartItem,
        category: "checkout page",
        userId,
      });
    }
  }, [cartItems, checkoutBlockedReason, userId]);

  if (!cartItemsExist) {
    // * CTA should link to finder normally.
    let label = "find local shops";
    let href = "/dispensaries";

    // * Embedded checkout links back to menu
    if (isEmbedded && embeddedMenuUrl) {
      label = "back to menu";
      href = embeddedMenuUrl;
    }
    return <EmptyBagMessage label={label} href={href} />;
  }

  return (
    <div id="checkout-bag" data-testid="bag-container">
      {cartChanges?.length > 0 && (
        <div className="container relative">
          <BagMessage
            className="elevation-low toast"
            message={
              cartChanges?.length === 1
                ? "An item in your cart has been updated"
                : `${cartChanges?.length} items in your cart have been updated`
            }
          />
        </div>
      )}

      <BagHeader
        isEmbedded={isEmbedded}
        isCartEmpty={!cartItemsExist}
        isInStoreCart={cartIsInStore}
      />

      <div
        id="column-wrapper"
        className="container flex flex-col md:flex-row gap-4 pb-lg"
      >
        <div className="md:basis-1/2 lg:basis-full flex flex-col gap-4 lg:mr-xl">
          {invalidCartItems && (
            <InvalidItemsNotification invalidItems={invalidCartItems} />
          )}

          {cartItems.length && dealGroups.length ? (
            <>
              <DealGroupContainer
                groups={dealGroups}
                errors={cartItemErrors}
                isEmbedded={isEmbedded}
                embeddedMenuUrl={embeddedMenuUrl}
                embeddedQuery={embeddedQuery}
              />
              <Prop65Message />
            </>
          ) : (
            <div
              data-testid="empty-bag-alert"
              className="text-sm pb-lg empty-bag-alert-container"
            >
              There’s nothing in your cart.
            </div>
          )}

          {isLoggedIn && isMobile && !cartIsInStore && (
            <ReorderCarousel
              className="my-lg"
              dispensaryId={dispensary.id}
              isBagPage={true}
              itemIdsToExclude={cartItems.map(({ menuItemId }) => menuItemId)}
              title="Buy it again"
            />
          )}
        </div>

        <div
          className="md:basis-1/2 lg:basis-[354px] lg:shrink-0"
          id="bag-order-confirmation"
        >
          <div className="bg-white p-lg flex flex-col gap-4">
            <FulfillmentCards isEmbedded={isEmbedded} />

            {!cartIsInStore &&
              (isLoggedIn ? <PromoCodeInput /> : <PromoCodeCTA />)}

            {shouldRenderFreeDeliveryBanner && (
              <>
                <div className="py-lg">
                  <FreeDeliveryStatus
                    subtotal={cartTotals.subtotal}
                    savings={cartTotals.savings}
                    freeDeliveryMinimum={
                      deliveryServiceArea?.freeDeliveryMinimum
                    }
                  />
                </div>
                <hr className="border-light-grey" />
              </>
            )}

            <div className="py-sm">
              <CartTotals totals={cartTotals} />
            </div>

            {selectedForDeliveryOption &&
              !!hasNotMetMinimumForDelivery &&
              deliveryServiceArea && (
                <div className="text-error">
                  {`Order does not meet ${currencyFormatter(
                    deliveryServiceArea.orderMinimum,
                    countryCode
                  )} minimum. You only need `}
                  <span className="font-bold">
                    {currencyFormatter(
                      Number(deliveryServiceArea.orderMinimum) -
                        postSavingsSubTotal,
                      countryCode
                    )}
                  </span>
                  {` more to complete your order!`}
                </div>
              )}

            {!!cartItemErrors?.length && (
              <TrackImpression category="bag" label="unavailable items">
                <p className="text-error">
                  Some of your items are unavailable. You’ll need to remove them
                  before checking out.
                </p>
              </TrackImpression>
            )}

            {/* TODO Remove when AZ Rec delivery becomes legal */}
            {dispensary?.state === "AZ" && selectedForDeliveryOption && (
              <AlertBanner
                message="Delivery orders can only be placed by medical customers in
              Arizona and require Medical ID information in checkout."
                icon={<AlertIcon className="text-yellow w-16" />}
              />
            )}

            {canProceedToCheckout && !cartIsInStore && (
              <ProceedToCheckoutButton isEmbedded={isEmbedded} />
            )}

            {cartIsInStore && (
              <>
                <InStoreOrderingMessage />
                {googleMapsDirectionsUrl && (
                  <DispensaryDirectionsButton
                    googleMapsUrl={googleMapsDirectionsUrl}
                    onClick={() => {
                      publishEvent({
                        action: Action.click,
                        category: Category.checkoutPage,
                        dispensaryId: dispensary?.id,
                        label: Label.inStoreGetDirections,
                      });
                    }}
                  />
                )}
              </>
            )}

            <Button
              href={returnHref}
              variant="text"
              className="text-sm text-default py-sm mx-auto"
              onClick={() => {
                trackCheckoutEvent("Click", "Keep Shopping", {
                  category: "Checkout Page",
                });
              }}
            >
              continue shopping
            </Button>
          </div>

          <FinePrint
            className="mt-lg mb-20"
            showTerms={false}
            country={country}
          />
        </div>
      </div>

      {isLoggedIn && !isMobile && !cartIsInStore && (
        <ReorderCarousel
          className="container pb-xxl pt-lg"
          dispensaryId={dispensary.id}
          isBagPage={true}
          itemIdsToExclude={cartItems.map(({ menuItemId }) => menuItemId)}
          title="Buy it again"
        />
      )}
    </div>
  );
};

export default Bag;
